import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Response from getting a pre-signed upload URL
 */
export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  s3Url: string;
  fileName: string;
}

/**
 * Uploaded file info to include in API requests
 */
export interface UploadedFileInfo {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileExtension: string;
  s3Key: string;
  s3Url: string;
}

/**
 * File Upload Service
 *
 * Handles file uploads to S3 via pre-signed URLs.
 * The flow is:
 * 1. Request a pre-signed URL from the API
 * 2. Upload the file directly to S3 using the pre-signed URL
 * 3. Include the S3 key/URL in subsequent API calls
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get a pre-signed URL for uploading a service appointment attachment
   */
  async getServiceAppointmentUploadUrl(filename: string): Promise<UploadUrlResponse> {
    return firstValueFrom(
      this.http.post<UploadUrlResponse>(
        `${this.apiUrl}/file-uploads/service-appointment`,
        { filename }
      )
    );
  }

  /**
   * Upload a file to S3 using a pre-signed URL
   */
  async uploadToS3(file: File, uploadUrl: string): Promise<void> {
    // Upload directly to S3 using fetch (bypasses Angular's HttpClient interceptors)
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  /**
   * Upload a file and get the S3 info for API submission
   * This is a convenience method that combines getUploadUrl + uploadToS3
   */
  async uploadServiceAppointmentFile(file: File): Promise<UploadedFileInfo> {
    // Step 1: Get pre-signed URL
    const urlInfo = await this.getServiceAppointmentUploadUrl(file.name);

    // Step 2: Upload to S3
    await this.uploadToS3(file, urlInfo.uploadUrl);

    // Step 3: Return the file info for API submission
    return {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileExtension: file.name.split('.').pop() || '',
      s3Key: urlInfo.s3Key,
      s3Url: urlInfo.s3Url,
    };
  }

  /**
   * Upload multiple files for a service appointment
   */
  async uploadServiceAppointmentFiles(files: File[]): Promise<UploadedFileInfo[]> {
    const results: UploadedFileInfo[] = [];

    for (const file of files) {
      const info = await this.uploadServiceAppointmentFile(file);
      results.push(info);
    }

    return results;
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Check if a file is an allowed type for service appointments
   * Allowed: images, PDFs, documents
   */
  isAllowedFileType(file: File): boolean {
    const allowedTypes = [
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      // PDFs
      'application/pdf',
      // Documents
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
    ];

    // Must match VALID_DRONE_RESOURCE_EXTENSIONS on the Hub and the `accept`
    // attribute on any input feeding this service.
    const allowedExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'webp',
      'pdf',
      'doc', 'docx', 'xls', 'xlsx', 'txt',
    ];

    const extension = this.getFileExtension(file.name);

    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
  }

  /**
   * Get max file size in bytes (20MB)
   */
  getMaxFileSize(): number {
    return 20 * 1024 * 1024; // 20MB
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
}
